import type { AdminMeResponse } from '@immo/api-contract';
import { apiClient } from './api-client';
import { setAccessToken } from './access-token-store';

export type CompleteAdminSignInOutcome =
  { readonly kind: 'success' } | { readonly kind: 'not-admin' } | { readonly kind: 'failed'; readonly status: number };

export async function fetchMe(): Promise<AdminMeResponse | null> {
  // No manual refresh here — the api-client fetcher does a proactive refresh
  // when no in-memory token is present, and dedupes against any concurrent
  // refresh fired by child route loaders. Calling refresh from here too
  // would race past the inflight guard and result in duplicate refresh
  // round-trips.
  const result = await apiClient.adminAuth.me();

  if (result.ok) {
    return result.body;
  }

  if (result.status === 401) {
    return null;
  }

  throw new Error(`Unexpected /admin/auth/me status ${result.status}`);
}

export async function startSignIn(): Promise<void> {
  const result = await apiClient.adminAuth.signIn({ query: { provider: 'google' } });

  if (!result.ok) {
    throw new Error(`Admin sign-in URL request failed: ${result.status}`);
  }

  window.location.assign(result.body.url);
}

export async function completeSignIn(code: string, state: string | null): Promise<CompleteAdminSignInOutcome> {
  const result = await apiClient.adminAuth.callback({ body: { code, ...(state !== null ? { state } : {}) } });

  if (result.ok) {
    setAccessToken(result.body.accessToken);

    return { kind: 'success' };
  }

  if (result.body.error.code === 'admin.auth.user-not-an-admin') {
    return { kind: 'not-admin' };
  }

  return { kind: 'failed', status: result.status };
}

export async function signOut(): Promise<string> {
  // Backend pins returnTo via `ADMIN_APP_ORIGIN` so each env's sign-out
  // lands on its own `/sign-in` rather than the WorkOS dashboard fallback
  // (which is the prod URL). No client-supplied returnTo to keep WorkOS
  // off any untrusted redirect surface.
  const result = await apiClient.adminAuth.signOut({ body: {} });
  setAccessToken(null);

  if (!result.ok) {
    throw new Error(`Admin sign-out failed: ${result.status}`);
  }

  return result.body.url;
}
